import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {from, Observable, throwError} from 'rxjs';
import {concatMap, toArray} from 'rxjs/operators';
import {ApplicantModel} from '@service/models';
import {ApplicantService} from '@service/services';
import {ProgressButtonOptions} from '@service/models/interfaces/progress-button.interface';
import {CSDataLayerService} from '@service/services/server/csdatalayer.service';

/** One entry of FinalSubmitResponseVO.missingFieldApplicants. */
export interface MissingFieldApplicant {
    applicantId: number;
    applicantType: string;
    firstName: string;
    lastName: string;
    missingFields: string[];
}

export interface MissingFieldDialogData {
    missingFieldApplicants: MissingFieldApplicant[];
    /** Full applicant models, sent back whole through updateApplicant. */
    applicants: ApplicantModel[];
    /** Re-runs final submit with the unchanged finance payload. */
    resubmit: () => Observable<any>;
}

/**
 * Result the dialog closes with: response on a successful re-submit, error when the re-submit itself failed,
 * cancelled when the applicant backed out. The caller runs its normal success / error handling on it.
 */
export interface MissingFieldDialogResult {
    response?: any;
    error?: any;
    cancelled?: boolean;
}

interface MissingSsnEntry {
    applicant: MissingFieldApplicant;
    ssn: string;
}

/**
 * Collects mandatory fields the server rejected final submit for (code 6xxx), saves them and re-submits.
 * Only SSN is collected today. Separate from AskSsnComponent, which belongs to the bureau retry flow.
 * SSN values must never be logged or tracked from here.
 */
@Component({
    selector: 'm-missing-field',
    templateUrl: './missing-field.component.html',
    styleUrls: ['./missing-field.component.scss']
})
export class MissingFieldComponent {
    static readonly SSN_FIELD = 'ssn';

    barButtonOptions: ProgressButtonOptions = {
        active: false,
        text: 'Continue',
        buttonColor: 'primary',
        // @ts-ignore
        flat: true,
        stroked: false,
        disabled: true
    };
    @ViewChild('f') form: NgForm;
    entries: MissingSsnEntry[] = [];
    errorMessage: string = null;

    constructor(
        public dialogRef: MatDialogRef<MissingFieldComponent, MissingFieldDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: MissingFieldDialogData,
        private applicantService: ApplicantService,
        private csDataLayerService: CSDataLayerService,
        private elRef: ElementRef
    ) {
        this.setEntries(data.missingFieldApplicants);
    }

    /** Final submit rejected because mandatory fields are missing. Comes back as HTTP 200. */
    static isMissingFieldsResponse(res: any): boolean {
        return !!res && res.code >= 6000 && res.code < 7000;
    }

    private static digitsOnly(value: string): string {
        return (value || '').toString().replace(/\D/g, '');
    }

    private static errorMessageOf(err: any): string {
        const error = typeof err === 'function' ? err() : err;
        return error?.message || error?.error?.message;
    }

    roleLabel(applicantType: string): string {
        switch (applicantType) {
            case 'P':
                return 'Primary';
            case 'S':
                return 'Joint owner';
            case 'G':
                return 'Guarantor';
            default:
                return 'Applicant';
        }
    }

    fullName(applicant: MissingFieldApplicant): string {
        return [applicant.firstName, applicant.lastName].filter(name => !!name && name.trim()).join(' ');
    }

    updateButtonState() {
        this.barButtonOptions.disabled = !this.isValid();
    }

    onSubmit(buttonId?: string) {
        this.csDataLayerService.trackButtonClick(buttonId, this.elRef.nativeElement.nodeName);
        if (this.barButtonOptions.active || !this.isValid()) {
            return;
        }
        this.barButtonOptions.active = true;
        this.errorMessage = null;

        // one at a time, every applicant save hits the same application
        from(this.entries).pipe(
            concatMap(entry => this.saveSsn(entry)),
            toArray()
        ).subscribe(() => {
            this.resubmit();
        }, err => {
            this.barButtonOptions.active = false;
            this.errorMessage = MissingFieldComponent.errorMessageOf(err) || 'We could not save the information. Please try again.';
        });
    }

    onCancel(buttonId?: string) {
        this.csDataLayerService.trackButtonClick(buttonId, this.elRef.nativeElement.nodeName);
        if (this.barButtonOptions.active) {
            return;
        }
        this.dialogRef.close({cancelled: true});
    }

    private resubmit() {
        this.data.resubmit().subscribe(res => {
            if (MissingFieldComponent.isMissingFieldsResponse(res)) {
                // still missing, stay open with only the applicants the server listed this time
                this.barButtonOptions.active = false;
                this.errorMessage = res.message || 'Some required information is still missing.';
                this.setEntries(res.missingFieldApplicants);
                return;
            }
            this.dialogRef.close({response: res});
        }, error => {
            this.dialogRef.close({error});
        });
    }

    private saveSsn(entry: MissingSsnEntry): Observable<any> {
        const applicant = (this.data.applicants || []).find(a => a && a.applicantId === entry.applicant.applicantId);
        if (!applicant) {
            return throwError(() => ({message: 'We could not find the applicant to update. Please go back and try again.'}));
        }
        // copy so the SSN is not left on the shared keyIdentifier applicant
        const updated = {...applicant, ssn: MissingFieldComponent.digitsOnly(entry.ssn)} as ApplicantModel;
        return this.applicantService.updateApplicant(applicant.applicationId, updated);
    }

    private setEntries(missingFieldApplicants: MissingFieldApplicant[]) {
        this.entries = (missingFieldApplicants || [])
            .filter(a => (a.missingFields || []).includes(MissingFieldComponent.SSN_FIELD))
            .map(a => ({applicant: a, ssn: ''}));
        this.updateButtonState();
    }

    private isValid(): boolean {
        // form validity carries the creditsnap-ssn validator (000 / 666 / 9xx / 00 / 0000 / repeated digits)
        return this.entries.length > 0 &&
            (!this.form || this.form.valid !== false) &&
            this.entries.every(entry => MissingFieldComponent.digitsOnly(entry.ssn).length === 9);
    }
}
